// zpost-app/src/components/ensapp/DomainMinter.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useBridgeTransaction from '../../hooks/useBridgeTransaction';
import { getRPCProvider } from '../../utils/rpc';

const DomainMinter = ({ integrationEnabled, receivedAddress, onDomainMinted, identityAuth }) => {
  const [subdomain, setSubdomain] = useState('');
  const [isMinting, setIsMinting] = useState(false);
  const [error, setError] = useState('');
  const [mintFee, setMintFee] = useState("0.015");
  const [mintCount, setMintCount] = useState(0);
  // const [contract, setContract] = useState(null);

  // Get address from props
  const address = identityAuth?.walletAddress || null;

  // Hooks
  const { wallet: authWallet, connectToZwallet } = useAuth();
  const { sendTransaction: bridgeSend } = useBridgeTransaction();

  // Receipt modal state (unused for now, but ready for future)
  const [receiptTxHash, setReceiptTxHash] = useState(null);

  // Ref to keep authWallet up‑to‑date
  const authWalletRef = useRef(authWallet);
  useEffect(() => {
    authWalletRef.current = authWallet;
    console.log('authWalletRef updated:', authWallet?.isConnected);
  }, [authWallet]);

  // RPC provider (unused for now, but ready)
  const [stableProvider, setStableProvider] = useState(null);

  // Initialize provider on mount
  useEffect(() => {
    let cancelled = false;
    const initProvider = async () => {
      try {
        const provider = await getRPCProvider();
        if (!cancelled) setStableProvider(provider);
      } catch (err) {
        console.error('Failed to initialize RPC provider:', err);
        setError('Could not connect to Ethereum network. Please refresh.');
      }
    };
    initProvider();
    return () => { cancelled = true; };
  }, []);

  console.log('🟢 DomainMinter mounted - SAFE MODE (L1 Deployment Pending)');
  console.log('🟢 Props received:', {
    integrationEnabled,
    receivedAddress: receivedAddress ? 'YES' : 'NO',
    onDomainMinted: onDomainMinted ? 'YES' : 'NO',
    identityAuth: identityAuth ? 'YES' : 'NO'
  });

  // Contract initialization - COMMENTED OUT FOR NOW
  useEffect(() => {
    console.log('📝 DomainMinter: Contract initialization skipped - L1 deployment pending');

    // Simulate contract data for UI
    setMintFee("0.015");
    setMintCount(0);

    /* L1 DEPLOYMENT PENDING - WILL UNCOMMENT WHEN CONTRACT IS ON ETHEREUM MAINNET
    const initContract = async () => {
      if (!address) return;

      try {
        console.log('🟡 Starting contract initialization...');
        const provider = new JsonRpcProvider(INFURA_URL);
        const contractInstance = new Contract(CONTRACT_ADDRESS, contractABI, provider);

        setContract(contractInstance);

        // Get current mint fee from contract
        const fee = await contractInstance.mintFee();
        const formattedFee = formatEther(fee);
        setMintFee(formattedFee);

        // Get user's mint count
        const count = await contractInstance.mintCount(address);
        setMintCount(Number(count));

        console.log('✅ Contract initialized successfully');

      } catch (err) {
        console.error('❌ Failed to initialize contract:', err);
        setError('Failed to connect to minting service: ' + err.message);
      }
    };

    initContract();
    */
  }, [address]);

  // Validation function - KEPT FOR UI/UX
  const validateSubdomain = (label) => {
    if (!label || label.length < 3) {
      return 'Subdomain must be at least 3 characters';
    }
    if (label.length > 63) {
      return 'Subdomain must be less than 64 characters';
    }

    const isValid = /^[a-z0-9-]{3,63}$/.test(label) && !label.startsWith('-') && !label.endsWith('-');
    if (!isValid) {
      return 'Subdomain can only contain a-z, 0-9, and hyphens. Cannot start or end with hyphen.';
    }
    return null;
  };

  // Pre-mint conditions check - COMMENTED OUT FOR NOW
  const checkPreMintConditions = async () => {
    console.log('📝 DomainMinter: Pre-mint check skipped - L1 deployment pending');
    return { success: true }; // Always pass for now

    /* L1 DEPLOYMENT PENDING
    if (!contract || !subdomain || !address) {
      return { success: false, error: 'Missing required data' };
    }

    const validationError = validateSubdomain(subdomain);
    if (validationError) {
      return { success: false, error: validationError };
    }

    try {
      const labelHash = ethers.keccak256(ethers.toUtf8Bytes(subdomain));
      const userMintCount = await contract.mintCount(address);
      const isLabelMinted = await contract.mintedLabels(labelHash);
      const currentMintFee = await contract.mintFee();

      if (userMintCount >= 5) {
        return { success: false, error: "Maximum 5 subdomains per wallet reached." };
      }
      if (isLabelMinted) {
        return { success: false, error: `Subdomain '${subdomain}.zpost.eth' is already minted.` };
      }

      const sentFee = parseEther(mintFee);
      const expectedFee = currentMintFee;

      if (sentFee.toString() !== expectedFee.toString()) {
        return { success: false, error: `Incorrect fee. Expected: ${formatEther(currentMintFee)} ETH, sent: ${mintFee} ETH` };
      }

      return { success: true };

    } catch (err) {
      console.error('❌ Pre-mint check failed:', err);
      return { success: false, error: "Failed to check mint conditions: " + err.message };
    }
    */
  };

  const handleSubdomainChange = (e) => {
    const value = e.target.value.replace(/[^a-z0-9-]/g, "").toLowerCase().substring(0, 63);
    setSubdomain(value);

    // Clear error when user types
    if (error) {
      const validationError = validateSubdomain(value);
      if (!validationError) {
        setError('');
      }
    }
  };

  // Mint function - SIMULATION ONLY (will later use bridge)
  const handleMint = async () => {
    if (!address) {
      setError('Please connect your wallet first');
      return;
    }

    const validationError = validateSubdomain(subdomain);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsMinting(true);
    setError('');

    try {
      const fullDomain = `${subdomain}.zpost.eth`;
      console.log('🔄 DomainMinter: SIMULATED mint process for:', fullDomain);

      // Simulate pre-mint conditions (always pass for now)
      const conditionsResult = await checkPreMintConditions();
      console.log('🔍 Pre-mint conditions (simulated):', conditionsResult);

      // SIMULATED MINTING PROCESS - NO BLOCKCHAIN INTERACTION
      setTimeout(() => {
        console.log('🎉 DomainMinter: SIMULATED mint success:', fullDomain);

        // Notify parent component
        if (onDomainMinted) {
          onDomainMinted(fullDomain, address);
        }

        // Show success message
        alert(`🎉 SUCCESS: ${fullDomain} would be minted! (Simulation - L1 contract deployment pending)`);

        // Reset form
        setSubdomain('');
        setIsMinting(false);
      }, 1500);

    } catch (err) {
      console.error('❌ Simulated minting failed:', err);
      setError('Simulated minting failed: ' + err.message);
      setIsMinting(false);
    }

    /* L1 DEPLOYMENT PENDING - REAL MINTING LOGIC
    try {
      const fullDomain = `${subdomain}.zpost.eth`;
      console.log('🔄 Starting REAL mint process:', fullDomain);

      // Check conditions against contract
      const conditionsMet = await checkPreMintConditions();
      if (!conditionsMet) {
        setIsMinting(false);
        return;
      }

      // REAL: Send transaction to ZPost Wallet for signing
      const transaction = {
        type: 'MINT_DOMAIN',
        domainName: subdomain,
        address: address,
        mintFee: mintFee,
        contractAddress: CONTRACT_ADDRESS
      };

      // Open ZPost Wallet for transaction signing
      if (window.opener) {
        window.opener.postMessage({
          type: 'SIGN_TRANSACTION',
          transaction: transaction,
          source: 'identity-app',
          timestamp: Date.now()
        }, 'https://zpost.ai');
      }

      // Update mint count optimistically
      setMintCount(prev => prev + 1);

      // Notify main app
      if (onDomainMinted) {
        onDomainMinted(fullDomain, address);
      }

      alert(`🎉 Transaction submitted for ${fullDomain}!`);
      setSubdomain('');

    } catch (err) {
      console.error('❌ Minting failed:', err);
      setError(err.message || 'Minting failed. Please try again.');
    } finally {
      setIsMinting(false);
    }
    */
  };

  // Safe early return
  if (!identityAuth) {
    return (
      <div className="minter-container">
        <h2 style={{ color: 'var(--gold)', textAlign: 'center' }}>Mint New .zpost.eth Domain</h2>
        <div style={{
          background: 'rgba(255, 68, 68, 0.1)',
          border: '1px solid #ff4444',
          color: '#ff4444',
          padding: '20px',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '20px'
        }}>
          <h3>🚨 Authentication Error</h3>
          <p>Wallet authentication is not available.</p>
        </div>
      </div>
    );
  }

  const validationError = validateSubdomain(subdomain);
  const isFormValid = !validationError && subdomain.length >= 3 && address && !isMinting;

  return (
    <div className="minter-container">
      <h2 style={{ color: 'var(--gold)', textAlign: 'center', marginBottom: '20px' }}>
        🎯 Mint New .zpost.eth Domain
      </h2>

      {/* Deployment Status Banner */}
      <div style={{
        background: 'rgba(255, 215, 0, 0.1)',
        border: '1px solid var(--gold)',
        padding: '12px',
        borderRadius: '6px',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        <p style={{ color: 'var(--gold)', margin: '0', fontWeight: 'bold' }}>
          ZPost ENS Subdomain Registrar
        </p>
        <p style={{ color: 'var(--text-light)', margin: '5px 0 0 0', fontSize: '14px' }}>
          Human-readable address across ZPost ecosystem.
        </p>
      </div>

      <div style={{
        background: 'var(--card-bg)',
        padding: '25px',
        borderRadius: '10px',
        border: '1px solid var(--gold)',
        boxShadow: '0 4px 8px var(--gold-shadow)'
      }}>
        <p style={{
          color: 'var(--text-secondary)',
          textAlign: 'center',
          marginBottom: '20px',
          fontSize: '16px'
        }}>
          Create your exclusive ZPost identity. Maximum 5 domains per wallet.
        </p>

        <input
          type="text"
          value={subdomain}
          onChange={handleSubdomainChange}
          placeholder="Enter your desired name (e.g., alice)"
          onKeyPress={(e) => {
            if (e.key === 'Enter' && isFormValid) {
              handleMint();
            }
          }}
          style={{
            width: '100%',
            padding: '12px',
            background: 'var(--black)',
            color: 'var(--gold)',
            border: `1px solid ${error ? '#ff4444' : 'var(--gold)'}`,
            borderRadius: '6px',
            fontSize: '16px',
            marginBottom: '1rem'
          }}
        />

        {subdomain && (
          <p style={{
            textAlign: 'center',
            color: 'var(--gold)',
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}>
            Your domain: <strong>{subdomain}.zpost.eth</strong>
            {validationError && (
              <span style={{ color: '#ff4444', display: 'block', fontSize: '0.8rem', marginTop: '5px' }}>
                ⚠️ {validationError}
              </span>
            )}
          </p>
        )}

        {error && (
          <div style={{
            background: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid #ff4444',
            color: '#ff4444',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '1rem',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <button
          onClick={handleMint}
          disabled={!isFormValid}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            fontWeight: 'bold',
            background: !isFormValid ? '#333' : 'var(--gold)',
            color: !isFormValid ? '#666' : 'var(--black)',
            border: 'none',
            borderRadius: '6px',
            cursor: !isFormValid ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {isMinting ? '🔄 Simulating Mint...' : `Simulate Mint ${subdomain || 'Domain'} for ${mintFee} ETH`}
        </button>

        <div style={{
          marginTop: '20px',
          padding: '15px',
          background: 'var(--card-bg)',
          borderRadius: '8px',
          border: '1px solid var(--border)',
          fontSize: '14px'
        }}>
          <p style={{ color: 'var(--text-light)', margin: '5px 0' }}><strong>Fee:</strong> {mintFee} ETH + gas</p>
          <p style={{ color: 'var(--text-light)', margin: '5px 0' }}><strong>Gas Estimate:</strong> ~0.0009 ETH</p>
          <p style={{ color: 'var(--text-light)', margin: '5px 0' }}><strong>Rules:</strong> 3-63 characters, a-z, 0-9, hyphens only</p>
          <p style={{ color: 'var(--text-light)', margin: '5px 0' }}><strong>Minted:</strong> {mintCount}/5 domains</p>
          <p style={{ color: 'var(--text-light)', margin: '5px 0' }}><strong>Connected Wallet:</strong> {address ? `${address.slice(0, 8)}...${address.slice(-6)}` : 'Not connected'}</p>
          <p style={{ color: 'var(--gold)', margin: '5px 0', fontWeight: 'bold' }}><strong>Status:</strong> Registry: ENS Mainnet Registry</p>
        </div>
      </div>

      {/* Receipt Modal (pre‑built for future use) */}
      {receiptTxHash && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.95)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--card-bg)',
            padding: '30px',
            borderRadius: '15px',
            border: '2px solid var(--gold)',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 0 50px rgba(255,215,0,0.3)'
          }}>
            <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px', textAlign: 'center' }}>
              Transaction Confirmed
            </h3>
            <div style={{
              background: 'var(--black)',
              padding: '20px',
              borderRadius: '10px',
              border: '1px solid var(--gold)',
              marginBottom: '20px'
            }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>
                Your transaction has been successfully mined.
              </p>
              {receiptTxHash.blockNumber && (
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ color: 'var(--gold)' }}>Block:</strong>
                  <span style={{ color: 'var(--text-light)', marginLeft: '5px' }}>
                    {receiptTxHash.blockNumber}
                  </span>
                </div>
              )}
              <div style={{
                background: 'rgba(255,215,0,0.1)',
                padding: '10px',
                borderRadius: '5px',
                wordBreak: 'break-all',
                marginBottom: '15px'
              }}>
                <a
                  href={`https://etherscan.io/tx/${receiptTxHash.hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--gold)', textDecoration: 'underline' }}
                >
                  {receiptTxHash.hash}
                </a>
              </div>
              <a
                href={`https://etherscan.io/tx/${receiptTxHash.hash}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  background: 'var(--gold)',
                  color: 'var(--black)',
                  padding: '10px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  marginBottom: '10px'
                }}
              >
                View on Etherscan
              </a>
            </div>
            <button
              onClick={() => setReceiptTxHash(null)}
              style={{
                width: '100%',
                background: 'transparent',
                color: '#ff4444',
                border: '1px solid #ff4444',
                padding: '15px',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainMinter;
